import { FC, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { getOrganizationPageNewsletterUrl } from "@simplyk/common";

import { useOpenedNewsletter } from "./helpers/useOpenedNewsletter";
import { NewsletterDrawer } from "./NewsletterDrawer/NewsletterDrawer";
import { NewsletterTile } from "./NewsletterTile/NewsletterTile";

import { useTranslate } from "@/common/hooks/useTranslate";
import { OrganizationPageOutput } from "@/common/types/trpc";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ChevronDown } from "@/icons/outlined";

const NEWSLETTERS_PER_PAGE = 6;

interface OrganizationPageNewslettersProps {
  organizationId: string;
  organizationPageUrl: string;
  newsletters: OrganizationPageOutput["newsletters"];
  isPreview?: boolean;
}

export const OrganizationPageNewsletters: FC<OrganizationPageNewslettersProps> = ({
  organizationId,
  organizationPageUrl,
  newsletters,
  isPreview = false,
}) => {
  const { t } = useTranslate();
  const [visibleCount, setVisibleCount] = useState(NEWSLETTERS_PER_PAGE);
  const { openedNewsletterId, openNewsletter, closeNewsletter } = useOpenedNewsletter({
    organizationId,
    organizationPageUrl,
  });

  const visibleNewsletters = newsletters.slice(0, visibleCount);
  const hasMoreNewsletters = newsletters.length > visibleCount;

  return (
    <Stack direction="column" alignItems={{ sm: "center" }} gap={{ xs: 2, sm: 3 }} data-test="org-page-newsletters">
      <FormTypography variant="h6" vibe="brand" sx={{ textTransform: "uppercase" }}>
        {t("organization", "ourLatestNewsletters")}
      </FormTypography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
          gap: 2,
          width: "100%",
        }}
      >
        {visibleNewsletters.map((newsletter) => (
          <NewsletterTile
            key={newsletter.id}
            subject={newsletter.subject}
            href={isPreview ? undefined : getOrganizationPageNewsletterUrl(organizationPageUrl, newsletter.id)}
            onClick={(event) => {
              event.preventDefault();
              if (!isPreview) {
                void openNewsletter(newsletter.id);
              }
            }}
          />
        ))}
      </Box>

      {hasMoreNewsletters && (
        <Stack direction="row" justifyContent="center" sx={{ marginTop: { xs: 3, sm: 2 } }}>
          <FormButton
            variant="ghost"
            onClick={() => setVisibleCount((count) => count + NEWSLETTERS_PER_PAGE)}
            endIcon={<ChevronDown />}
          >
            {t("organization", "newsletters.viewMore")}
          </FormButton>
        </Stack>
      )}

      {!isPreview && (
        <NewsletterDrawer
          organizationId={organizationId}
          newsletterId={openedNewsletterId}
          onClose={() => void closeNewsletter()}
        />
      )}
    </Stack>
  );
};
